import React from 'react'
import _ from 'lodash'
import { Operation, TableOfContents, Markdown, Schema, Divider, BackReferenceList } from '..'
import * as styles from './styles'
import { Row, ContentColumn, CodeColumn, DividerHeader } from '../../styles/utils'
import {
  refNameToPretty,
  bestStringify,
  sectionToSlug,
  sectionToTitle
} from '../../util'

const Operations = (props) => {
  const { content, openapi, operations } = props
  return (
    <React.Fragment>
      {
      _.map(operations, (operationMetadata) => {
        const httpMethod = operationMetadata.method
        const { path } = operationMetadata
        const operation = openapi.getPath(openapi.bundle, ['paths', path, httpMethod])

        return (
          <React.Fragment key={`${httpMethod}-${path}`}>
            <Operation

              path={path}
              openapi={openapi}
              httpMethod={httpMethod}
              content={content}
              operationMetadata={operationMetadata}
              operation={operation}
            />
            <Divider />
          </React.Fragment>
        )
      })
    }
    </React.Fragment>
  )
}


const SchemaExampleComponents = (content, schema) => {
  let examples = []
  if (schema.examples) {
    examples = schema.examples
  } else if (schema.example) {
    examples = [schema.example]
  }

  const exampleContents = _.map(examples, (example) => {
    return content.templates.schemaExample({
      exampleJson: bestStringify(example, { allowSingleLine: true })
    })
  })
  return (
    <CodeColumn>
      {
        examples.length > 0
          ? (
            <p><b>Example{ examples.length > 1 ? 's' : '' }</b></p>
          )
          : <p>Examples coming later.</p>
      }

      {
        _.map(exampleContents, (c, i) => <Markdown key={i}>{c}</Markdown>)
      }
    </CodeColumn>
  )
}

const SchemaComponents = (props) => {
  const { content, openapi, schema } = props

  const prettyRefName = refNameToPretty(content, schema.__refName)
  return (
    <React.Fragment key={props.slug}>
      <Row>
        <ContentColumn>
          <h3 id={props.slug}>{prettyRefName}</h3>
        </ContentColumn>
        <CodeColumn />
      </Row>
      <Row>
        <ContentColumn>

          <Markdown shiftHeadingLevel={3} ast={schema.descriptionMarkdownAst} />

          <DividerHeader>Format</DividerHeader>
          <Schema openapi={openapi} content={content} schema={schema} />
        </ContentColumn>
        {SchemaExampleComponents(content, schema)}
      </Row>
      <Row>
        <BackReferenceList content={content} refName={schema.__refName} />
      </Row>
    </React.Fragment>
  )
}


const QueryLanguageSection = (props) => {
  const { content, openapi } = props

  return (
    <React.Fragment>
      {
        _.map(content.queryLanguage, (section, sectionIndex) => {
          const schemas = _.map(section.schemas, (refName) => {
            const schema = openapi.resolveRef({ $ref: `#/components/schemas/${refName}` })
            if (!schema) {
              throw new Error(`Unable to find schema ${refName}`)
            }
            return schema
          })

          return (
            <React.Fragment key={sectionIndex}>
              <Row>
                <ContentColumn>
                  <h2 id={sectionToSlug(section)}>{sectionToTitle(section)}</h2>
                  <Markdown shiftHeadingLevel={2} ast={section.descriptionMarkdownAst} />
                </ContentColumn>
                <CodeColumn />
              </Row>

              {
                _.map(schemas, (schema, i) => {
                  const slug = section.schemaSlugs[i]

                  return (
                    <React.Fragment key={slug}>
                      <SchemaComponents slug={slug} content={content} openapi={openapi} schema={schema} />
                      <Divider />
                    </React.Fragment>
                  )
                })
              }
            </React.Fragment>
          )
        })
      }
    </React.Fragment>
  )
}

const DatabaseSchemaSection = (props) => {
  const { content, openapi} = props
  return (
    <React.Fragment>
      {
        _.map(content.databaseSchema, (section, sectionIndex) => {
          const schemas = _.map(section.schemas, (refName) => {
            const schema = openapi.resolveRef({ $ref: `#/components/schemas/${refName}` })
            if (!schema) {
              throw new Error(`Unable to find schema ${refName}`)
            }
            return schema
          })

          let sectionHeader = 
            <h2 id={sectionToSlug(section)}>{sectionToTitle(section)}</h2>

          let sectionContentRow = 
            <Row>
              <ContentColumn>
                <Markdown shiftHeadingLevel={2} ast={section.descriptionMarkdownAst} />
              </ContentColumn>
              <CodeColumn />
            </Row>
          
          if (section.schemaRef) {
            const sectionSchema = openapi.resolveRef({ $ref: `#/components/schemas/${section.schemaRef}` })
            if (!sectionSchema) {
              throw new Error(`Unable to find section schema ref ${section.schemaRef}`)
            }

            sectionHeader = <h2 id={sectionToSlug(section)}>{section.schemaRef}</h2>

            sectionContentRow = 
              <Row>
                <ContentColumn>
                  <Markdown shiftHeadingLevel={2} ast={sectionSchema.descriptionMarkdownAst} />
                  <DividerHeader>Format</DividerHeader>
                  <Schema openapi={openapi} content={content} schema={sectionSchema} />
                </ContentColumn>
                {SchemaExampleComponents(content, sectionSchema)}
              </Row>
          }

          return (
            <React.Fragment key={sectionIndex}>
              <Row>
                <ContentColumn>
                  {sectionHeader}
                </ContentColumn>
                <CodeColumn />
              </Row>
              {sectionContentRow}

              {
                _.map(schemas, (schema, i) => {
                  const slug = section.schemaSlugs[i]

                  return (
                    <React.Fragment key={slug}>
                      <SchemaComponents slug={slug} content={content} openapi={openapi} schema={schema} />
                      <Divider />
                    </React.Fragment>
                  )
                })
              }
            </React.Fragment>
          )
        })
      }
    </React.Fragment>
  )
}

export default ({ openapi, content }) => {
  const dataOperations = _.filter(content.operations, o => o.apiCategory === 'data')
  const queryOperations = _.filter(content.operations, o => o.apiCategory === 'query')

  return (
    <styles.Container>
      <styles.TocContainer>
        <TableOfContents content={content} openapi={openapi} />
      </styles.TocContainer>

      <styles.Content>
        {
          _.map(content.markdown.base.layout, (block, i) => {
            switch (block.type) {
              case 'dataOperationsSection':
                return (
                  <Operations key={i} content={content} openapi={openapi} operations={dataOperations} />
                )
              case 'queryOperationsSection':
                return (
                  <Operations key={i} content={content} openapi={openapi} operations={queryOperations} />
                )
              case 'databaseSchemaSection':
                return <DatabaseSchemaSection key={i} content={content} openapi={openapi} />
              case 'queryLanguageSection':
                return <QueryLanguageSection key={i} content={content} openapi={openapi} />
              default:
                return (
                  <Row key={i}>
                    <ContentColumn>
                      <Markdown ast={block.contentCol} />
                    </ContentColumn>
                    <CodeColumn>
                      <Markdown ast={block.codeCol} />
                    </CodeColumn>
                  </Row>
                )
            }
          })
        }
      </styles.Content>
    </styles.Container>
  )
}
